import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Picture } from '@ycos/picture'
import { createOrGetVideo } from '@ycos/brightcove'
import { createComponent } from '@ycos/fela'
import MuteUnMuteButton from './MuteUnMuteButton'

const VideoContainer = createComponent('VideoContainer', () => ({
  maxWidth: '100%',
  height: '100%',
  display: 'block',
  position: 'relative'
}))

const Poster = createComponent('Poster', ({ showPoster, additionalStyles }) => ({
  ...additionalStyles,
  width: '100%',
  height: '100%',
  opacity: showPoster ? 1 : 0,
  transition: 'opacity 500ms cubic-bezier(0.3, 0.0, 0.3, 1.0)'
}))

const AmbientVideo = ({
  account,
  playerId,
  videoId,
  pictures,
  imageSphere,
  lazy,
  loop,
  playsInline,
  autoPlay,
  posterStyles,
  pictureStyles,
  allowMute = false,
  muteCTAs,
  fetchPriority,
  isPictureFullWidth
}) => {
  const videoRef = useRef(null)
  const wrapperRef = useRef(null)
  const [visible, setVisible] = useState(false)
  const [showPoster, setShowPoster] = useState(true)
  const [videoLoaded, setVideoLoaded] = useState(false)
  const [videoInstance, setVideoInstance] = useState(null)
  const [muted, setMuted] = useState(true)

  const videoStyle = {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    objectFit: 'cover',
    // Prevents black line when the image and video have different aspect ratios
    backgroundColor: 'transparent'
  }

  useEffect(() => {
    const options = {
      rootMargin: '0px',
      threshold: 0.5
    }
    const observer = new IntersectionObserver(([entry]) => {
      setVisible(entry.isIntersecting)
    }, options)

    observer.observe(wrapperRef.current)

    if (videoRef.current) {
      createOrGetVideo(account, playerId, videoRef.current).then((videoInstance) => {
        if (!videoInstance.error_) {
          setVideoInstance(videoInstance)
          videoInstance.catalog.getVideo(videoId, () => {
            videoInstance.on('canplay', () => {
              setVideoLoaded(true)
            })
            if (visible && videoLoaded) {
              setShowPoster(false)
              videoInstance.play()
            } else {
              videoInstance.pause()
            }
          })
        }
      })
    }

    return () => {
      if (videoRef.current) {
        observer.unobserve(wrapperRef.current)
      }
    }
  }, [videoRef, visible, videoId, pictures, videoLoaded])

  return (
    <VideoContainer>
      <div ref={wrapperRef}>
        <video-js
          ref={videoRef}
          preload="none"
          autoPlay={autoPlay}
          data-video-id={videoId}
          data-account={account}
          data-player={playerId}
          data-embed="default"
          data-application-id
          className="video-js"
          style={videoStyle}
          loop={loop}
          playsInline={playsInline}
        />
        {videoLoaded && !showPoster && allowMute && (
          <MuteUnMuteButton
            onClick={(ev) => {
              videoInstance.muted(!videoInstance.muted())
              setMuted(videoInstance.muted())
              ev.preventDefault() // prevents wrapper link being triggered
            }}
            muted={muted}
            CTAs={muteCTAs}
          />
        )}
        <Poster showPoster={showPoster} additionalStyles={posterStyles}>
          {isPictureFullWidth ? (
            <Picture fetchPriority={fetchPriority} images={pictures} imagesphere={imageSphere} lazy={lazy} style={pictureStyles} widths={[1500, 3000]} />
          ) : (
            <Picture fetchPriority={fetchPriority} images={pictures} imagesphere={imageSphere} lazy={lazy} style={pictureStyles} />
          )}
        </Poster>
      </div>
    </VideoContainer>
  )
}

AmbientVideo.propTypes = {
  account: PropTypes.string.isRequired,
  playerId: PropTypes.string.isRequired,
  videoId: PropTypes.string.isRequired,
  pictures: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  lazy: PropTypes.bool,
  imageSphere: PropTypes.shape({
    quality: PropTypes.number
  }),
  playsInline: PropTypes.bool,
  posterStyles: PropTypes.shape(),
  pictureStyles: PropTypes.shape(),
  allowMute: PropTypes.bool,
  muteCTAs: PropTypes.shape({
    muteVideo: PropTypes.string,
    unMuteVideo: PropTypes.string
  }),
  fetchPriority: PropTypes.string
}

AmbientVideo.defaultProps = {
  lazy: true,
  imageSphere: {
    quality: 65
  },
  autoPlay: false,
  posterStyles: {},
  pictureStyles: {}
}

export default AmbientVideo
